import { istFeiertag } from './feiertage'

/**
 * **Wann jemand arbeitet** ([Z-19](../../../docs/phasen/zusatz-19-tagesplan.md) AP 19.4).
 *
 * Der Tagesplan rechnete bis zum 01.10.2026 mit einer fest verdrahteten Zahl: Mo–Fr, 8 bis 17
 * Uhr, anderthalb Stunden Grundlast. Für Nils stimmt das ungefähr – für Teilzeit stimmt es
 * nicht, und ein Plan, der den halben Tag verplant, den jemand gar nicht hat, ist schlimmer als
 * gar keiner. Er ist nämlich **glaubwürdig** falsch.
 *
 * ## Warum Stunden als Dezimalzahl
 *
 * `9.5` ist halb zehn. Der Plan rechnet überall so (`luecken`, `tagBelegen`, `freiNetto`), und
 * eine zweite Einheit daneben wäre an jeder Grenze eine Umrechnung und damit eine Fehlerquelle.
 * In der **Datenbank** stehen Minuten seit Mitternacht, weil dort eine ganze Zahl robuster ist
 * als eine Kommazahl – umgerechnet wird genau hier, an einer Stelle.
 *
 * ## Warum die Vorgabe in `shared` steht
 *
 * Weil beide Seiten sie brauchen und sie dieselbe sein muss: Die Oberfläche zeichnet das Raster,
 * und `gefaehrdet()` rechnet die Vorschau über Wochen. Zwei Hausmaße, die auseinanderlaufen,
 * wären zwei verschiedene Aussagen über denselben Tag.
 */

/** Minuten seit Mitternacht → Stunden als Dezimalzahl. `480` wird zu `8`. */
const inStunden = (minuten: number): number => minuten / 60

/** Das Hausmaß, solange niemand etwas anderes eingestellt hat. */
export const ARBEITSZEIT_HAUSMASS = {
  von: 8,
  bis: 17,
  grundlast: 1.5,
  /** Montag zuerst, Sonntag zuletzt. */
  tage: '1111100',
} as const

export interface Arbeitszeit {
  von: number
  bis: number
  /** Abzug je Arbeitstag für Mail, Telefon, Unterbrechung – in Stunden. */
  grundlast: number
  /** Sieben Zeichen, Montag zuerst: `1111100`. */
  tage: string
}

/** Was an der Person steht – alles freiwillig, `null` heißt Hausmaß. */
export interface ArbeitszeitFelder {
  arbeitVon?: number | null
  arbeitBis?: number | null
  grundlastMinuten?: number | null
  arbeitstage?: string | null
}

/**
 * **Die wirksame Arbeitszeit einer Person.**
 *
 * Jedes Feld fällt **einzeln** auf das Hausmaß zurück, nicht als Block: Wer nur die Grundlast
 * ändert, soll nicht nebenbei seine Arbeitszeiten verlieren.
 *
 * **Unsinnige Werte werden verworfen, nicht übernommen.** Eine Spanne, die rückwärts läuft
 * (`bis <= von`), ergäbe einen Tag ohne jede freie Minute – der Plan bliebe stumm leer, und
 * niemand käme auf die Idee, dass eine falsche Einstellung schuld ist. Dann lieber das
 * Hausmaß und ein sichtbar normaler Tag.
 */
export function arbeitszeitVon(felder: ArbeitszeitFelder | null | undefined): Arbeitszeit {
  const von =
    typeof felder?.arbeitVon === 'number' && felder.arbeitVon >= 0 && felder.arbeitVon < 24 * 60
      ? inStunden(felder.arbeitVon)
      : ARBEITSZEIT_HAUSMASS.von
  const bis =
    typeof felder?.arbeitBis === 'number' && felder.arbeitBis > 0 && felder.arbeitBis <= 24 * 60
      ? inStunden(felder.arbeitBis)
      : ARBEITSZEIT_HAUSMASS.bis
  const grundlast =
    typeof felder?.grundlastMinuten === 'number' && felder.grundlastMinuten >= 0
      ? inStunden(felder.grundlastMinuten)
      : ARBEITSZEIT_HAUSMASS.grundlast
  const tage =
    typeof felder?.arbeitstage === 'string' && /^[01]{7}$/.test(felder.arbeitstage)
      ? felder.arbeitstage
      : ARBEITSZEIT_HAUSMASS.tage

  if (bis <= von) {
    return { ...ARBEITSZEIT_HAUSMASS, grundlast, tage }
  }
  /* Eine Grundlast, die den ganzen Tag frisst, ist ebenfalls eine Fehleingabe. */
  return { von, bis, grundlast: Math.min(grundlast, bis - von), tage }
}

/**
 * **Ist dieser Kalendertag für diese Person ein Arbeitstag?**
 *
 * Drei Bedingungen, in dieser Reihenfolge: ein gewählter Wochentag, **und** kein gesetzlicher
 * Feiertag ([`feiertage.ts`](./feiertage.ts)). Der Feiertag steht hinten, weil er die seltenere
 * Ausnahme ist – und weil er nicht verhandelbar ist: Wer den Samstag als Arbeitstag einträgt,
 * arbeitet trotzdem nicht an Ostern.
 */
export function istArbeitstag(zeit: Arbeitszeit, d: Date): boolean {
  /* `getDay()` zählt ab Sonntag; die Zeichenkette beginnt bei Montag. */
  const index = (d.getDay() + 6) % 7
  if (zeit.tage[index] !== '1') return false
  return !istFeiertag(d)
}

/** Die freie Zeit eines Arbeitstages nach Abzug der Grundlast – in Stunden. */
export function nettoStunden(zeit: Arbeitszeit): number {
  return Math.max(0, zeit.bis - zeit.von - zeit.grundlast)
}

/* ── Mehrere Fenster an einem Tag ──────────────────────────────────────── */

/** Felder, die an einer **Mitgliedschaft** stehen können – alle freiwillig. */
export interface WeltzeitFelder {
  id: string
  arbeitVon?: number | null
  arbeitBis?: number | null
  arbeitstage?: string | null
}

/** Ein Zeitfenster des Tages, zusammen mit der Welt, für die es gilt. */
export interface Tagesfenster {
  von: number
  bis: number
  /** `null` = das allgemeine Fenster; es gilt für alle Welten **ohne** eigene Zeit. */
  weltId: string | null
}

/** Hat diese Welt überhaupt eine abweichende Zeit? Ein einziges gesetztes Feld genügt. */
export function hatEigeneZeit(welt: WeltzeitFelder): boolean {
  return (
    typeof welt.arbeitVon === 'number' ||
    typeof welt.arbeitBis === 'number' ||
    (typeof welt.arbeitstage === 'string' && /^[01]{7}$/.test(welt.arbeitstage))
  )
}

/**
 * **Die wirksame Zeit in einer Arbeitswelt** – Welt schlägt Person, Feld für Feld.
 *
 * Wer in PRIVAT nur die **Tage** umstellt (Sa/So), behält dort seine gewohnten Stunden; wer nur
 * die **Stunden** umstellt (abends), behält die gewohnten Tage. Alles andere wäre eine
 * Einstellung, die stillschweigend eine zweite mitändert.
 *
 * **Die Grundlast kommt immer von der Person.** Mail, Telefon und Unterbrechung fallen einmal am
 * Tag an, nicht einmal je Welt.
 */
export function arbeitszeitDerWelt(
  person: ArbeitszeitFelder | null | undefined,
  welt: WeltzeitFelder,
): Arbeitszeit {
  return arbeitszeitVon({
    arbeitVon: welt.arbeitVon ?? person?.arbeitVon ?? null,
    arbeitBis: welt.arbeitBis ?? person?.arbeitBis ?? null,
    arbeitstage: welt.arbeitstage ?? person?.arbeitstage ?? null,
    grundlastMinuten: person?.grundlastMinuten ?? null,
  })
}

/**
 * **Welche Fenster dieser Tag hat** ([Z-19](../../../docs/phasen/zusatz-19-tagesplan.md)
 * AP 19.4, 01.10.2026).
 *
 * Nils: *„Meine PRIVAT habe ich bspw. Samstag/Sonntag und in der Woche abends ein wenig."*
 *
 * Damit ist ein Tag nicht mehr **ein** Zeitraum, sondern mehrere – und in jeden darf nur, was
 * zu seiner Welt gehört. Am Samstag entsteht nur das PRIVAT-Fenster, also plant Work dort auch
 * nur Privates; Dienstagabend kommt es **zusätzlich** zum allgemeinen Fenster dazu.
 *
 * ## Warum Welten ohne eigene Zeit kein eigenes Fenster bekommen
 *
 * Sie teilen sich das allgemeine. Jeder Welt ein eigenes Fenster mit denselben Grenzen zu geben
 * hieße, den Tag so oft zu zählen, wie es Welten gibt – aus acht freien Stunden würden bei drei
 * Welten vierundzwanzig. Der Tag ist **einer**; die Fenster teilen ihn, sie vervielfachen ihn
 * nicht.
 */
export function fensterDesTages(
  person: ArbeitszeitFelder | null | undefined,
  welten: readonly WeltzeitFelder[],
  datum: Date,
): Tagesfenster[] {
  const allgemein = arbeitszeitVon(person)
  const fenster: Tagesfenster[] = []

  if (istArbeitstag(allgemein, datum)) {
    fenster.push({ von: allgemein.von, bis: allgemein.bis, weltId: null })
  }

  for (const welt of welten) {
    if (!hatEigeneZeit(welt)) continue
    const zeit = arbeitszeitDerWelt(person, welt)
    if (!istArbeitstag(zeit, datum)) continue
    fenster.push({ von: zeit.von, bis: zeit.bis, weltId: welt.id })
  }

  return fenster.sort((a, b) => a.von - b.von || a.bis - b.bis)
}

/**
 * **In welches Fenster eine Aufgabe darf.**
 *
 * Gehört ihre Welt zu denen mit eigener Zeit, darf sie **nur** dorthin – wer PRIVAT auf
 * Samstag und Abend legt, will seine Gartenarbeit nicht Dienstag um zehn eingeplant sehen. Alle
 * übrigen teilen sich das allgemeine Fenster.
 */
export function fensterFuer(
  weltId: string | null | undefined,
  mitEigenerZeit: ReadonlySet<string>,
): string | null {
  return weltId && mitEigenerZeit.has(weltId) ? weltId : null
}

/**
 * **Den Tag auf die Hälfte kürzen, die noch Arbeitszeit ist**
 * ([Z-19](../../../docs/phasen/zusatz-19-tagesplan.md) AP 19.5, 02.10.2026).
 *
 * Gekürzt wird **jedes** Fenster an seiner eigenen Mitte, nicht der Tag an einer gemeinsamen
 * Uhrzeit. Wer PRIVAT abends von 19 bis 21 Uhr führt, hat an einem halben Urlaubstag dort eine
 * Stunde – nicht null, weil „Mittag" längst vorbei ist, und nicht zwei, weil der Urlaub nur das
 * Büro beträfe. Die Mitte eines Fensters ist die ehrlichste Teilung, die ohne Uhrzeit aus der
 * Schnittstelle möglich ist.
 *
 * `'nichts'` gibt eine leere Liste zurück – der Tagesplan behandelt sie wie einen Feiertag.
 */
export function fensterKuerzen(
  fenster: readonly Tagesfenster[],
  verbleibt: 'nichts' | 'vormittag' | 'nachmittag',
): Tagesfenster[] {
  if (verbleibt === 'nichts') return []
  return fenster.map((f) => {
    const mitte = f.von + (f.bis - f.von) / 2
    return verbleibt === 'vormittag' ? { ...f, bis: mitte } : { ...f, von: mitte }
  })
}
